import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ExternalLink, Loader2, RefreshCw, Lock, Share2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SYNC_ROLES = ['campaign_manager', 'manager', 'admin', 'account_executive'];

const LinkBtn = ({ href, icon: Icon, label, testid, title }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    title={title}
    className="rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5 border border-black/[0.12] dark:border-white/[0.08] bg-white dark:bg-[#1A1D24] text-[#0F0F0F] dark:text-white hover:border-[#FF451A]/40 transition-colors"
    data-testid={testid}
  >
    <Icon size={13} /> {label}
  </a>
);

export const SheetSyncControls = ({ campaignId, role }) => {
  const [info, setInfo] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const canSync = SYNC_ROLES.includes(role);

  useEffect(() => {
    if (!canSync) return;
    axios.get(`${API_URL}/api/bulk/campaigns/${campaignId}/sheet-info`, { withCredentials: true })
      .then(({ data }) => setInfo(data))
      .catch(() => setInfo(null));
  }, [campaignId, canSync]);

  if (!canSync) return null;

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/bulk/campaigns/${campaignId}/sync-sheet`, {}, { withCredentials: true });
      setInfo(data);
      toast.success('Sheet synced successfully', { description: `${data.synced_rows} creators written to both sheets` });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sheet sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="sheet-sync-controls">
      <button
        onClick={sync}
        disabled={syncing}
        className="rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5 border border-[#FF451A] text-[#FF451A] hover:bg-[#FF451A]/10 disabled:opacity-50 transition-colors"
        data-testid="sync-sheets-btn"
        title={info?.last_synced_at ? `Last synced ${new Date(info.last_synced_at).toLocaleString()}` : 'Create & sync Google Sheets'}
      >
        {syncing ? <Loader2 size={13} className="animate-spin" /> : info?.client_url ? <RefreshCw size={13} /> : <ExternalLink size={13} />}
        {syncing ? 'Syncing…' : 'Sync to Sheets'}
      </button>
      {info?.internal_url && (
        <LinkBtn href={info.internal_url} icon={Lock} label="Open Internal Sheet" testid="open-internal-sheet-btn" title="Private — costs, margins, contact details" />
      )}
      {info?.client_url && (
        <LinkBtn href={info.client_url} icon={Share2} label="Share with Client" testid="share-client-sheet-btn" title="Public view-only link for the client" />
      )}
    </div>
  );
};
